import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { CanonicalUrlService } from './canonical-url.service';
import { StructuredDataService } from './structured-data.service';

interface RouteMetadata {
  title?: string;
  description?: string;
  'og:title'?: string;
  'og:description'?: string;
  'og:image'?: string;
  'og:type'?: string;
  'twitter:title'?: string;
  'twitter:description'?: string;
  'twitter:image'?: string;
  'twitter:card'?: string;
}

@Injectable({ providedIn: 'root' })
export class MetaGuard implements CanActivate {
  constructor(
    private title: Title,
    private meta: Meta,
    private canonicalUrl: CanonicalUrlService,
    private structuredData: StructuredDataService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    this.canonicalUrl.setFromUrl(state.url || '/');

    const data = route.data as { title?: string; meta?: RouteMetadata };
    const metaData = data.meta || {};
    const pageTitle = metaData.title || data.title;
    if (pageTitle) this.title.setTitle(pageTitle);

    const description = metaData.description;
    const image = metaData['og:image'] || metaData['twitter:image'];
    const tags: Array<{ key: string; value?: string; attribute: 'name' | 'property' }> = [
      { key: 'description', value: description, attribute: 'name' },
      { key: 'og:title', value: metaData['og:title'] || pageTitle, attribute: 'property' },
      { key: 'og:description', value: metaData['og:description'] || description, attribute: 'property' },
      { key: 'og:image', value: metaData['og:image'] || image, attribute: 'property' },
      { key: 'og:type', value: metaData['og:type'] || 'website', attribute: 'property' },
      { key: 'twitter:title', value: metaData['twitter:title'] || pageTitle, attribute: 'name' },
      { key: 'twitter:description', value: metaData['twitter:description'] || description, attribute: 'name' },
      { key: 'twitter:image', value: metaData['twitter:image'] || image, attribute: 'name' },
      { key: 'twitter:card', value: metaData['twitter:card'] || (image ? 'summary_large_image' : 'summary'), attribute: 'name' },
    ];

    tags.forEach(({ key, value, attribute }) => {
      const selector = `${attribute}="${key}"`;
      if (value) {
        const definition = attribute === 'property'
          ? { property: key, content: value }
          : { name: key, content: value };
        this.meta.updateTag(definition, selector);
        const duplicateAttribute = attribute === 'property' ? 'name' : 'property';
        this.meta.removeTag(`${duplicateAttribute}="${key}"`);
      } else {
        this.meta.removeTag(selector);
      }
    });

    this.structuredData.updatePage();
    return true;
  }
}
